/**
 * MyJourneyHeaderButton — Feb 2026 R138c
 *
 * Renders a compact Rocket/GraduationCap icon in the AppShell header
 * whenever the signed-in user has an active onboarding or offboarding
 * journey. Shows a red badge with the count of pending tasks + recent
 * comments; clicking navigates to /app/my/journey.
 *
 * Polls every 60s so a new comment / task shows up without a page
 * refresh — cheap because the backend endpoint is 2 `count_documents`
 * calls.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Rocket, GraduationCap } from "lucide-react";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

export default function MyJourneyHeaderButton() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/me/journey/summary").catch(() => ({ data: {} })),
          api.get("/me/onboarding/approvals?status=pending").catch(() => ({ data: { count: 0 } })),
        ]);
        if (!cancelled) setSummary({ ...(s.data || {}), pending_approvals: a.data?.count || 0 });
      } catch { /* not fatal */ }
    };
    load();
    // Feb 2026 R281 · Visibility-gated polling.
    const stopPoll = pollWhenVisible(load, 60000);
    return () => { cancelled = true; stopPoll(); };
  }, []);

  if (!summary || (!summary.has_onboarding && !summary.has_offboarding && !summary.pending_approvals)) return null;
  const Icon = summary.has_offboarding && !summary.has_onboarding ? GraduationCap : Rocket;
  const badge = (summary.pending_tasks || 0) + (summary.unread_comments || 0) + (summary.pending_approvals || 0);
  const targetLink = summary.pending_approvals && !summary.has_onboarding && !summary.has_offboarding
    ? "/app/my/approvals"
    : "/app/my/journey";
  const tip = summary.pending_approvals
    ? `${summary.pending_approvals} approval${summary.pending_approvals === 1 ? "" : "s"} waiting on you`
    : (summary.has_offboarding && !summary.has_onboarding
      ? "My Offboarding"
      : "My Journey");

  return (
    <Link
      to={targetLink}
      className="relative inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-primary hover:bg-muted rounded-none"
      title={tip}
      data-testid="header-my-journey"
    >
      <Icon className="w-4 h-4" />
      {badge > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[9px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center leading-none"
          data-testid="header-my-journey-badge"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}
